'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/local-api/client';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import InviteFriends from '@/components/InviteFriends';
import {
  UserPlusIcon,
  UserMinusIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  UserGroupIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface UserProfile {
  id: string;
  full_name: string;
  username?: string;
  bio?: string;
  role?: string;
  followers_count: number;
  following_count: number;
  posts_count: number;
  avatar_url?: string;
}

type Tab = 'friends' | 'requests' | 'discover';

const gradients = [
  'from-blue-500 to-purple-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-red-500',
  'from-pink-500 to-rose-600',
  'from-violet-500 to-indigo-600',
  'from-amber-500 to-orange-600',
];
const getGradient = (id: string) => gradients[id.charCodeAt(0) % gradients.length];
const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

export default function FriendsContent() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [friends, setFriends] = useState<UserProfile[]>([]);
  const [pendingRequests, setPendingRequests] = useState<{ id: string; sender: UserProfile }[]>([]);
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());
  const [discoverPeople, setDiscoverPeople] = useState<UserProfile[]>([]);
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [following, setFollowing] = useState<Set<string>>(new Set());
  const [connections, setConnections] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const supabase = createClient();

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    // Load connections (friends)
    const { data: conns } = await supabase
      .from('connections')
      .select('user_id_1, user_id_2')
      .or(`user_id_1.eq.${user.id},user_id_2.eq.${user.id}`);

    const friendIds = (conns ?? []).filter((c: any) => c.user_id_1 === user.id || c.user_id_2 === user.id).map((c: any) =>
      c.user_id_1 === user.id ? c.user_id_2 : c.user_id_1
    );
    setConnections(new Set(friendIds));

    let friendProfiles: UserProfile[] = [];
    if (friendIds.length > 0) {
      const { data: fp } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, bio, role, followers_count, following_count, posts_count, avatar_url')
        .in('id', friendIds);
      friendProfiles = (fp as UserProfile[]) ?? [];
    }

    // Load pending received requests
    const { data: received } = await supabase
      .from('connection_requests')
      .select('id, sender_id')
      .eq('receiver_id', user.id)
      .eq('status', 'pending');

    let senderProfiles: UserProfile[] = [];
    if (received && received.length > 0) {
      const senderIds = received.map((r: any) => r.sender_id);
      const { data: sp } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, bio, role, followers_count, following_count, posts_count, avatar_url')
        .in('id', senderIds);
      senderProfiles = (sp as UserProfile[]) ?? [];
      const profileMap = new Map(senderProfiles.map((p: any) => [p.id, p]));
      setPendingRequests(
        received.map((r: any) => ({ id: r.id, sender: profileMap.get(r.sender_id) as UserProfile })).filter((r) => r.sender)
      );
    } else {
      setPendingRequests([]);
    }

    // Load sent requests
    const { data: sent } = await supabase
      .from('connection_requests')
      .select('receiver_id')
      .eq('sender_id', user.id)
      .eq('status', 'pending');
    setSentRequests(new Set((sent ?? []).map((s: any) => s.receiver_id)));

    // Load following
    const { data: followData } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id);
    setFollowing(new Set((followData ?? []).map((f: any) => f.following_id)));

    // Load discover people (exclude friends and self)
    const excludeIds = [user.id, ...friendIds];
    const { data: discover } = await supabase
      .from('user_profiles')
      .select('id, full_name, username, bio, role, followers_count, following_count, posts_count, avatar_url')
      .not('id', 'in', `(${excludeIds.join(',')})`)
      .order('followers_count', { ascending: false })
      .limit(30);
    const discoverProfiles = (discover as UserProfile[]) ?? [];

    // Compute real follower counts from follows table for all displayed users
    const allDisplayedIds = [
      ...friendProfiles.map((p) => p.id),
      ...senderProfiles.map((p) => p.id),
      ...discoverProfiles.map((p) => p.id),
    ];

    if (allDisplayedIds.length > 0) {
      const { data: followerRows } = await supabase
        .from('follows')
        .select('following_id')
        .in('following_id', allDisplayedIds);

      // Count followers per user
      const followerCountMap = new Map<string, number>();
      (followerRows ?? []).forEach((row: any) => {
        followerCountMap.set(row.following_id, (followerCountMap.get(row.following_id) ?? 0) + 1);
      });

      // Merge real counts into profiles
      const mergeCount = (p: UserProfile): UserProfile => ({
        ...p,
        followers_count: followerCountMap.get(p.id) ?? p.followers_count ?? 0,
      });

      setFriends(friendProfiles.map(mergeCount));
      setDiscoverPeople(discoverProfiles.map(mergeCount));

      if (received && received.length > 0) {
        const profileMap = new Map(senderProfiles.map((p) => [p.id, mergeCount(p)]));
        setPendingRequests(
          (received as any[]).map((r) => ({ id: r.id, sender: profileMap.get(r.sender_id) as UserProfile })).filter((r) => r.sender)
        );
      }
    } else {
      setFriends(friendProfiles);
      setDiscoverPeople(discoverProfiles);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel(`friends-${user.id}`)
      .on('postgres_changes', { event: '*', table: 'connections', filter: `user_id_1=eq.${user.id}` }, () => loadData())
      .on('postgres_changes', { event: '*', table: 'connections', filter: `user_id_2=eq.${user.id}` }, () => loadData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, loadData]);

  const handleSearch = useCallback(
    async (q: string) => {
      if (!q.trim()) { setSearchResults([]); return; }
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, bio, role, followers_count, following_count, posts_count, avatar_url')
        .neq('id', user?.id ?? '')
        .or(`full_name.ilike.%${q}%,username.ilike.%${q}%,bio.ilike.%${q}%`)
        .limit(20);
      setSearchResults((data as UserProfile[]) ?? []);
    },
    [user]
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 350);
  };

  const handleSendRequest = async (userId: string) => {
    if (!user) { toast.error('Sign in to connect'); return; }
    setProcessing(userId);
    const { error } = await supabase.from('connection_requests').upsert(
      {
        sender_id: user.id,
        receiver_id: userId,
        status: 'pending',
      },
      { onConflict: 'sender_id,receiver_id' }
    );
    if (error) {
      toast.error('Could not send request: ' + (error.message || 'Unknown error'));
    } else {
      setSentRequests((prev) => new Set([...prev, userId]));
      await supabase.from('follows').upsert({ follower_id: user.id, following_id: userId });
      setFollowing((prev) => new Set([...prev, userId]));
      toast.success('Friend request sent!');
    }
    setProcessing(null);
  };

  const handleAccept = async (requestId: string, senderId: string) => {
    setProcessing(requestId);
    const { error } = await supabase
      .from('connection_requests')
      .update({ status: 'accepted' })
      .eq('id', requestId);
    if (!error) {
      // Insert into connections (both directions handled by unique constraint)
      const uid1 = user!.id < senderId ? user!.id : senderId;
      const uid2 = user!.id < senderId ? senderId : user!.id;
      await supabase.from('connections').upsert({ user_id_1: uid1, user_id_2: uid2 });
      toast.success('Friend request accepted!');
      loadData();
    } else {
      toast.error('Could not accept request');
    }
    setProcessing(null);
  };

  const handleDecline = async (requestId: string) => {
    setProcessing(requestId);
    await supabase.from('connection_requests').update({ status: 'declined' }).eq('id', requestId);
    setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));
    toast.success('Request declined');
    setProcessing(null);
  };

  const handleUnfriend = async (friendId: string) => {
    if (!user) return;
    setProcessing(friendId);
    const uid1 = user.id < friendId ? user.id : friendId;
    const uid2 = user.id < friendId ? friendId : user.id;
    await supabase.from('connections').delete().eq('user_id_1', uid1).eq('user_id_2', uid2);
    await supabase.from('connection_requests')
      .delete()
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${friendId}),and(sender_id.eq.${friendId},receiver_id.eq.${user.id})`);
    setFriends((prev) => prev.filter((f) => f.id !== friendId));
    setConnections((prev) => { const s = new Set(prev); s.delete(friendId); return s; });
    toast.success('Removed from friends');
    setProcessing(null);
  };

  const handleFollow = async (userId: string) => {
    if (!user) { toast.error('Sign in to follow'); return; }
    setProcessing(userId);
    const { error } = await supabase.from('follows').upsert({ follower_id: user.id, following_id: userId });
    if (!error) {
      setFollowing((prev) => new Set([...prev, userId]));
      toast.success('Following!');
    } else {
      toast.error('Could not follow');
    }
    setProcessing(null);
  };

  const handleUnfollow = async (userId: string) => {
    if (!user) return;
    setProcessing(userId);
    await supabase.from('follows').delete().eq('follower_id', user.id).eq('following_id', userId);
    setFollowing((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    toast.success('Unfollowed');
    setProcessing(null);
  };

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'friends', label: 'My Friends', count: friends.length },
    { key: 'requests', label: 'Requests', count: pendingRequests.length },
    { key: 'discover', label: 'Discover People' },
  ];

  const displayDiscover = searchQuery.trim() ? searchResults : discoverPeople;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <UserGroupIcon className="text-primary" style={{ width: 32, height: 32 }} />
        </div>
        <p className="text-lg font-bold text-foreground">Sign in to see your friends</p>
        <p className="text-sm text-muted-foreground">Connect with people on the platform</p>
        <Link href="/login" className="px-6 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
          Sign In
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <InviteFriends />
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Friends</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your connections and discover new people</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted/50 rounded-xl mb-6 border border-border/50">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-card text-foreground shadow-sm border border-border/50'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                tab.key === 'requests' ?'bg-red-500 text-white'
                  : activeTab === tab.key
                  ? 'bg-primary/20 text-primary' :'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search bar (Discover tab) */}
      {activeTab === 'discover' && (
        <div className="relative mb-5">
          <MagnifyingGlassIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            style={{ width: 16, height: 16 }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Search by name, username, or bio..."
            className="w-full bg-muted border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
          />
        </div>
      )}

      {/* Search bar (Friends tab) */}
      {activeTab === 'friends' && (
        <div className="relative mb-5">
          <MagnifyingGlassIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            style={{ width: 16, height: 16 }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search your friends..."
            className="w-full bg-muted border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
          />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* ── FRIENDS TAB ── */}
          {activeTab === 'friends' && (
            <div>
              {friends.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <UsersIcon className="text-muted-foreground/50" style={{ width: 28, height: 28 }} />
                  </div>
                  <p className="text-base font-semibold text-foreground mb-1">No friends yet</p>
                  <p className="text-sm text-muted-foreground mb-4">Discover people and send friend requests</p>
                  <button
                    onClick={() => setActiveTab('discover')}
                    className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
                  >
                    Discover People
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {friends
                    .filter((f) =>
                      !searchQuery.trim() ||
                      f.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      f.username?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((friend) => (
                      <FriendCard
                        key={friend.id}
                        user={friend}
                        isFollowing={following.has(friend.id)}
                        processing={processing}
                        onUnfriend={handleUnfriend}
                        onFollow={handleFollow}
                        onUnfollow={handleUnfollow}
                      />
                    ))}
                </div>
              )}
            </div>
          )}

          {/* ── REQUESTS TAB ── */}
          {activeTab === 'requests' && (
            <div className="space-y-3">
              {pendingRequests.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <ClockIcon className="text-muted-foreground/50" style={{ width: 28, height: 28 }} />
                  </div>
                  <p className="text-base font-semibold text-foreground mb-1">No pending requests</p>
                  <p className="text-sm text-muted-foreground">Friend requests you receive will appear here</p>
                </div>
              ) : (
                pendingRequests.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center gap-3 p-4 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors"
                  >
                    <Link href={`/community-network/profile/${req.sender.id}`} className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getGradient(req.sender.id)} flex items-center justify-center text-white font-bold text-sm overflow-hidden`}>
                        {req.sender.avatar_url ? (
                          <img src={req.sender.avatar_url} alt={req.sender.full_name} className="w-full h-full object-cover" />
                        ) : (
                          getInitials(req.sender.full_name || 'U')
                        )}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link href={`/community-network/profile/${req.sender.id}`}>
                        <p className="text-sm font-bold text-foreground hover:text-primary transition-colors truncate">
                          {req.sender.full_name}
                        </p>
                      </Link>
                      {req.sender.username && (
                        <p className="text-[11px] text-muted-foreground">@{req.sender.username}</p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        <span className="font-semibold text-foreground">{req.sender.followers_count ?? 0}</span> followers
                        {req.sender.bio && ` · ${req.sender.bio.slice(0, 40)}${req.sender.bio.length > 40 ? '…' : ''}`}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleAccept(req.id, req.sender.id)}
                        disabled={processing === req.id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-[11px] font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        {processing === req.id ? (
                          <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <CheckIcon style={{ width: 12, height: 12 }} />
                        )}
                        Accept
                      </button>
                      <button
                        onClick={() => handleDecline(req.id)}
                        disabled={processing === req.id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
                      >
                        <XMarkIcon style={{ width: 12, height: 12 }} />
                        Decline
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── DISCOVER TAB ── */}
          {activeTab === 'discover' && (
            <div>
              {searchQuery.trim() && (
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Results for &quot;{searchQuery}&quot;
                </p>
              )}
              {!searchQuery.trim() && (
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  People you may know
                </p>
              )}
              {displayDiscover.length === 0 ? (
                <div className="text-center py-16">
                  <UsersIcon className="text-muted-foreground/40 mx-auto mb-3" style={{ width: 32, height: 32 }} />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery.trim() ? 'No users found' : 'No more people to discover'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {displayDiscover.map((person) => (
                    <DiscoverCard
                      key={person.id}
                      user={person}
                      isConnected={connections.has(person.id)}
                      isRequested={sentRequests.has(person.id)}
                      isFollowing={following.has(person.id)}
                      processing={processing}
                      onSendRequest={handleSendRequest}
                      onFollow={handleFollow}
                      onUnfollow={handleUnfollow}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Friend Card ──
interface FriendCardProps {
  user: UserProfile;
  isFollowing: boolean;
  processing: string | null;
  onUnfriend: (id: string) => void;
  onFollow: (id: string) => void;
  onUnfollow: (id: string) => void;
}

function FriendCard({ user, isFollowing, processing, onUnfriend, onFollow, onUnfollow }: FriendCardProps) {
  const isProcessing = processing === user.id;
  return (
    <div className="flex items-start gap-3 p-4 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors group">
      <Link href={`/community-network/profile/${user.id}`} className="flex-shrink-0">
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getGradient(user.id)} flex items-center justify-center text-white font-bold text-sm overflow-hidden hover:opacity-90 transition-opacity`}>
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" />
          ) : (
            getInitials(user.full_name || 'U')
          )}
        </div>
      </Link>
      <div className="flex-1 min-w-0">
        <Link href={`/community-network/profile/${user.id}`}>
          <p className="text-sm font-bold text-foreground hover:text-primary transition-colors truncate">{user.full_name}</p>
        </Link>
        {user.username && <p className="text-[11px] text-muted-foreground">@{user.username}</p>}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-foreground">{user.followers_count ?? 0}</span> followers
          </span>
          <span className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-foreground">{user.posts_count ?? 0}</span> posts
          </span>
        </div>
        {user.bio && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{user.bio}</p>}
      </div>
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500/10 text-green-600 text-[11px] font-semibold">
          <CheckIcon style={{ width: 11, height: 11 }} />
          Friends
        </span>
        {isFollowing ? (
          <button
            onClick={() => onUnfollow(user.id)}
            disabled={isProcessing}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
          >
            <UserMinusIcon style={{ width: 11, height: 11 }} />
            Unfollow
          </button>
        ) : (
          <button
            onClick={() => onFollow(user.id)}
            disabled={isProcessing}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-50"
          >
            Follow
          </button>
        )}
        <button
          onClick={() => onUnfriend(user.id)}
          disabled={isProcessing}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
        >
          {isProcessing ? (
            <span className="w-3 h-3 border border-current/30 border-t-current rounded-full animate-spin" />
          ) : (
            <UserMinusIcon style={{ width: 11, height: 11 }} />
          )}
          Unfriend
        </button>
      </div>
    </div>
  );
}

// ── Discover Card ──
interface DiscoverCardProps {
  user: UserProfile;
  isConnected: boolean;
  isRequested: boolean;
  isFollowing: boolean;
  processing: string | null;
  onSendRequest: (id: string) => void;
  onFollow: (id: string) => void;
  onUnfollow: (id: string) => void;
}

function DiscoverCard({ user, isConnected, isRequested, isFollowing, processing, onSendRequest, onFollow, onUnfollow }: DiscoverCardProps) {
  const isProcessing = processing === user.id;
  return (
    <div className="flex items-start gap-3 p-4 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors group">
      <Link href={`/community-network/profile/${user.id}`} className="flex-shrink-0">
        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getGradient(user.id)} flex items-center justify-center text-white font-bold text-sm overflow-hidden hover:opacity-90 transition-opacity`}>
          {user.avatar_url ? (
            <img src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" />
          ) : (
            getInitials(user.full_name || 'U')
          )}
        </div>
      </Link>
      <div className="flex-1 min-w-0">
        <Link href={`/community-network/profile/${user.id}`}>
          <p className="text-sm font-bold text-foreground hover:text-primary transition-colors truncate">{user.full_name}</p>
        </Link>
        {user.username && <p className="text-[11px] text-muted-foreground">@{user.username}</p>}
        <p className="text-[11px] text-muted-foreground capitalize mt-0.5">
          {user.role === 'admin' ? '⚡ Admin' : user.role === 'moderator' ? '🛡️ Moderator' : '👤 Member'}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-foreground">{user.followers_count ?? 0}</span> followers
          </span>
          <span className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-foreground">{user.posts_count ?? 0}</span> posts
          </span>
        </div>
        {user.bio && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{user.bio}</p>}
      </div>
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        {isConnected ? (
          <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500/10 text-green-600 text-[11px] font-semibold">
            <CheckIcon style={{ width: 11, height: 11 }} />
            Friends
          </span>
        ) : isRequested ? (
          <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold">
            <CheckIcon style={{ width: 11, height: 11 }} />
            Requested
          </span>
        ) : (
          <button
            onClick={() => onSendRequest(user.id)}
            disabled={isProcessing}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary text-white text-[11px] font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isProcessing ? (
              <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <UserPlusIcon style={{ width: 11, height: 11 }} />
            )}
            Add Friend
          </button>
        )}
        {isFollowing ? (
          <button
            onClick={() => onUnfollow(user.id)}
            disabled={isProcessing}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors disabled:opacity-50"
          >
            <UserMinusIcon style={{ width: 11, height: 11 }} />
            Unfollow
          </button>
        ) : (
          !isConnected && (
            <button
              onClick={() => onFollow(user.id)}
              disabled={isProcessing}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-muted text-muted-foreground text-[11px] font-semibold hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-50"
            >
              Follow
            </button>
          )
        )}
      </div>
    </div>
  );
}
